// Right-rail stay summary used on the guests and payment pages — the hotel
// counterpart of TripSummaryCard.
//
// It carries one thing the flight version doesn't: a **taxes-at-property** line.
// Those are collected by the hotel at check-in, so they are not part of what the
// partner's wallet is debited — but leaving them off entirely means a guest
// arrives to an unexpected bill. They are shown below the total, visibly outside
// it.

import { HoldTimer } from "./HoldTimer";
import {
  fmtBoard, fmtTaxDescription, fmtDateLong, fmtDeadline, fmtMoney, nightsLabel, occupancyLabel, starLabel,
} from "../lib/hotelFormat";
import type { HotelItinerary } from "../lib/hotelTypes";

export function HotelSummaryCard({
  booking,
  ctaLabel,
  onCta,
  hideCta = false,
  ctaDisabled = false,
  ctaLoading = false,
  helpText,
}: {
  booking: HotelItinerary;
  ctaLabel?: string;
  onCta?: () => void;
  hideCta?: boolean;
  ctaDisabled?: boolean;
  ctaLoading?: boolean;
  helpText?: React.ReactNode;
}) {
  const { hotel, room, pricing } = booking;
  const currency = pricing.currency;
  const stars = starLabel(hotel.star_rating);
  // The quote session's remaining life, not a held room: nothing is reserved
  // until /book/. It still matters — past it the offer has to be re-priced.
  const targetMs = booking.remaining_seconds != null
    ? Date.now() + booking.remaining_seconds * 1000
    : null;

  return (
    <aside className="booking-sidebar">
      <div className="summary-card">
        {/* Deliberately NOT "held for": no room is reserved until /book/. What
            is running out is the quote — past it the rate has to be re-priced,
            which is exactly what the payment page does. */}
        {targetMs != null && <HoldTimer targetMs={targetMs} prefix="Quote valid for" />}

        <h3>Stay <span className="serif">summary</span></h3>
        <div className="sub">
          {stars && <span className="hotel-stars">{stars}</span>}
          {hotel.name}
        </div>

        <div className="hotel-stay-dates">
          <div>
            <span className="lbl">Check-in</span>
            <b>{fmtDateLong(booking.check_in)}</b>
            {hotel.check_in_time && <span className="t">from {hotel.check_in_time}</span>}
          </div>
          <div className="hotel-stay-nights">{nightsLabel(booking.nights)}</div>
          <div>
            <span className="lbl">Check-out</span>
            <b>{fmtDateLong(booking.check_out)}</b>
            {hotel.check_out_time && <span className="t">by {hotel.check_out_time}</span>}
          </div>
        </div>

        <div className="trip-leg">
          <div className="dir">{room.name}</div>
          <div className="leg-meta">
            {fmtBoard(room.board_basis, room.board_name)} ·{" "}
            {occupancyLabel(booking.occupancies)}
          </div>
          <div className={`room-cancel${room.refundable ? " ok" : ""}`} style={{ marginTop: 8 }}>
            {room.free_cancellation_until
              ? <>Free cancellation until <b>{fmtDeadline(room.free_cancellation_until)}</b></>
              : room.cancellation_summary}
          </div>
        </div>

        <div className="fare-list">
          <div className="fare-line">
            <span className="lbl">
              Room rate · {nightsLabel(booking.nights)}
              {booking.occupancies.length > 1 ? ` × ${booking.occupancies.length}` : ""}
            </span>
            <span>{fmtMoney(pricing.base_fare, currency)}</span>
          </div>
          {pricing.per_night_fare != null && (
            <div className="fare-line">
              <span className="lbl">Per night</span>
              <span>{fmtMoney(pricing.per_night_fare, currency)}</span>
            </div>
          )}
          <div className="fare-line total">
            <span className="lbl">You'll pay</span>
            <span className="amt">{fmtMoney(pricing.final_fare, currency)}</span>
          </div>
          {!!pricing.taxes_at_property && (
            <div className="fare-line at-property-line">
              <span className="lbl">Payable at the property</span>
              <span>{fmtMoney(pricing.taxes_at_property, currency)}</span>
            </div>
          )}
        </div>

        {!!pricing.taxes_at_property_breakdown?.length && (
          <ul className="at-property-breakdown">
            {pricing.taxes_at_property_breakdown.map((tax, i) => (
              <li key={i}>
                <span>{fmtTaxDescription(tax.description)}</span>
                <span>{fmtMoney(tax.amount, tax.currency)}</span>
              </li>
            ))}
          </ul>
        )}

        {!hideCta && (
          <button
            type="button"
            className={`btn btn-yellow btn-pill btn-full${ctaLoading ? " loading" : ""}`}
            style={{ marginTop: 18 }}
            disabled={ctaDisabled || ctaLoading}
            onClick={onCta}
          >
            {ctaLabel || "Save & continue"}
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round"><path d="M5 12h14M13 5l7 7-7 7" /></svg>
          </button>
        )}

        {helpText && (
          <p className="field-help" style={{ marginTop: 12, textAlign: "center" }}>{helpText}</p>
        )}
      </div>
    </aside>
  );
}
